
import { faRectangleHistoryCircleUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Pagination, Stack } from '@mui/material';
import { MprcContainer, MprcIconButton, MprcRequestErrorReport, MprcSpinnerIndicator } from 'components';
import { MprcFilterBox, MprcFilterCount } from 'components/filter';
import { IconFilter } from 'components/icons';
import { MprcRequestSnackbarErrorReport } from 'components/MprcRequestErrorReport';
import { CoreFilterCommand, DevPlanUserDto, DevPlanUserDtoPaginatedList, DevPlanUsersListViewMode } from 'generated/CoreApi_v1';
import { useCoreApi } from 'providers/CoreApiProvider';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { LmsPaths } from 'routes/LmsRouter';
import { AdvancedFilter, IAdvFilterCommand, IAdvFilterParams } from 'views/general/AdvancedFilter';
import { FilterCmd } from 'views/general/AdvancedFilter/FilterCmd';
import { DevPlanUserCard } from 'views/lms/devPlan/Home/components/DevPlanUserCard';


////////////////////////////////////////////////////////////////////////////

export interface DevPlanListFilter extends IAdvFilterParams<any> {
    pageNumber: number;
}

interface IProps {
    headerTitle: React.ReactNode;
    filter: DevPlanListFilter;
    viewMode: DevPlanUsersListViewMode;
    onChangeFilter: (filter: Partial<DevPlanListFilter>) => void;
}

////////////////////////////////////////////////////////////////////////////

export const DevPlansBaseList: React.FC<IProps> = ({ headerTitle, filter, viewMode, onChangeFilter }) => {

    const api = useCoreApi();
    const navigate = useNavigate();

    const { t } = useTranslation("lms");

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>();
    const [viewData, setViewData] = useState<DevPlanUserDtoPaginatedList>();

    const filtered = filter.searchText != undefined && filter.searchText.length > 0;


    const loadData = () => {
        setError(null);
        setLoading(true);
        api.v1.devPlan.getUsersList({
            searchText: filter.searchText ?? null,
            filters: filter.advFilters,
            pageNumber: filter.pageNumber,
            viewMode,
            pageSize: 12
        })
            .then(response => setViewData(response.data))
            .catch(e => setError(e))
            .finally(() => setLoading(false));
    };

    ///
    const dropdownOptions = (): IAdvFilterCommand[] => {
        var options: IAdvFilterCommand[] = [
            FilterCmd.User.hasNotActiveDevPlan(1),
            FilterCmd.User.hasPendingApprovalDevPlan(1),
            FilterCmd.User.hasDraftDevPlan(1),
            FilterCmd.User.hasDelayedDevPlan(1),
        ];

        return options;
    };

    ///
    const handleResetFilters = () => {
        onChangeFilter({ searchText: "" });
    };

    const handleSwitchWithActivePlans = () => {
        if (!filter.advFilters.some(x => x.command == CoreFilterCommand.UserHasActiveDevPlan)) {
            onChangeFilter({
                pageNumber: 1,
                advFilters: [...filter.advFilters, {
                    command: CoreFilterCommand.UserHasActiveDevPlan,
                    hideChip: true,
                    value: null,
                    quickFilter: null
                }
                ]
            });
        }
        else {
            onChangeFilter({ pageNumber: 1, advFilters: filter.advFilters.filter(x => x.command != CoreFilterCommand.UserHasActiveDevPlan) });
        }
    };

    const handleGoToUserPlans = (item: DevPlanUserDto) => {
        navigate(LmsPaths.UserDevPlans + "/" + item.userId);
    };

    useEffect(loadData, [filter, viewMode]);

    return (
        <>
            <MprcContainer>
                <MprcFilterBox>
                    <AdvancedFilter
                        filter={filter}
                        leftDropdownAlign="end"
                        searchTextConfirmMode="onEnter"
                        placeholder={t("DevPlan.List.SearchTextPlaceholder")}
                        startComponent={headerTitle}
                        leftDropdowns={[{
                            filterCommands: dropdownOptions(),
                            textOrIcon: <IconFilter />,
                            title: t("shared:AdvFilter.Title")
                        }]}
                        onFilterChange={(filter) => onChangeFilter(filter)}
                        onError={error => MprcRequestSnackbarErrorReport(error)}
                        beforeLeftDropdown={[
                            <MprcIconButton
                                color={filter.advFilters.some(x => x.command == CoreFilterCommand.UserHasActiveDevPlan) ? "warning" : "muted"}
                                title={t("DevPlan.List.Users.ActivePlansTooltip")}
                                onClick={handleSwitchWithActivePlans}
                            >
                                <FontAwesomeIcon icon={faRectangleHistoryCircleUser} />
                            </MprcIconButton>
                        ]}
                    />
                </MprcFilterBox>
            </MprcContainer >
            <MprcContainer>
                <MprcRequestErrorReport error={error} />
                {loading
                    ? <MprcSpinnerIndicator />
                    :
                    <MprcContainer>
                        <MprcContainer>
                            <MprcFilterCount
                                loading={loading}
                                filter={{
                                    active: filtered,
                                    text: t("DevPlan.List.Users.RecordsFilteredText", { count: viewData?.totalRows ?? 0 }),
                                    onResetFilter: handleResetFilters
                                }}
                                isEmpty={viewData?.totalRows == 0}
                                countText={t("DevPlan.List.Users.RecordsFoundText", { count: viewData?.totalRows ?? 0 })}

                            />
                        </MprcContainer>
                        {viewData &&
                            <>
                                <MprcContainer margin="large">
                                    <Box
                                        display="grid"
                                        gridTemplateColumns="repeat(auto-fill, 220px)"
                                        justifyContent="space-between"
                                        gap={2}
                                    >
                                        {viewData.items.map(item =>
                                            <DevPlanUserCard
                                                key={item.userId}
                                                user={item}
                                                onCardClick={() => handleGoToUserPlans(item)}
                                            />
                                        )}
                                    </Box>
                                </MprcContainer>
                                {viewData.totalPages > 1 &&
                                    <Stack direction="row" justifyContent="center">
                                        <Pagination
                                            shape="rounded"
                                            color="primary"
                                            page={viewData.pageNumber}
                                            count={viewData.totalPages}
                                            onChange={(e, pageNumber) => onChangeFilter({ pageNumber })}
                                        />
                                    </Stack>
                                }
                            </>
                        }
                    </MprcContainer>
                }
            </MprcContainer>
        </>
    );
};
